// ============================================
// Путь: D:\QR\QR-WEB\dashboard\src\components\Business\BusinessDeposit.jsx
// Название: Business Deposit Component (EUR + USDT) - Multichain
// Версия: 2.0
// Дата: 30.11.2025
// ============================================

import React, { useState, useEffect } from 'react';
import './BusinessDeposit.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://qr-money.onrender.com';

const BusinessDeposit = () => {
  const [method, setMethod] = useState('eur');
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [depositInfo, setDepositInfo] = useState(null);
  const [networks, setNetworks] = useState([]);
  const [selectedNetwork, setSelectedNetwork] = useState('tron');
  const [calculation, setCalculation] = useState(null);
  const [createdDeposit, setCreatedDeposit] = useState(null);
  const [txHash, setTxHash] = useState('');
  const [history, setHistory] = useState([]);

  useEffect(() => {
    fetchDepositInfo();
    fetchHistory();
  }, []);

  const fetchDepositInfo = async () => {
    try {
      const response = await fetch(`${API_URL}/api/crypto/info`);
      const data = await response.json();
      if (data.success) {
        setDepositInfo(data.data.deposit);
        if (data.data.networks && data.data.networks.length > 0) {
          setNetworks(data.data.networks.filter(n => n.enabled));
          setSelectedNetwork(data.data.defaultNetwork || 'tron');
        }
      }
    } catch (err) {
      console.error('Error fetching deposit info:', err);
    }
  };

  const fetchHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/crypto/deposit/history`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setHistory(data.data.deposits || []);
      }
    } catch (err) {
      console.error('Error fetching history:', err);
    }
  };

  const getCurrentNetwork = () => networks.find(n => n.code === selectedNetwork) || null;

  const getUsdtLimits = () => {
    const network = getCurrentNetwork();
    if (network?.tokens) {
      const usdt = network.tokens.find(t => t.symbol === 'USDT');
      if (usdt?.deposit) {
        return { min: usdt.deposit.minAmount || 1, max: usdt.deposit.maxAmount || 10000 };
      }
    }
    return { min: depositInfo?.methods?.usdt?.minAmount || 1, max: depositInfo?.methods?.usdt?.maxAmount || 10000 };
  };

  const calculateEur = (eur) => {
    if (!depositInfo || !eur) return null;
    const rate = depositInfo.currentRates?.rateDeposit || 1.09;
    const spread = depositInfo.currentRates?.ourMarginPercent || 5;
    const bankFee = depositInfo.methods?.eur?.bankFee || 5;
    const usdt = (eur * rate) - (eur * rate * spread / 100) - (bankFee * rate);
    return { eur: eur.toFixed(2), usdt: usdt.toFixed(2), rate: rate.toFixed(4), spread, bankFee };
  };

  const handleAmountChange = (e) => {
    const value = e.target.value;
    setAmount(value);
    setError(null);

    if (method === 'eur' && value && !isNaN(value)) {
      const eur = parseFloat(value);
      const min = depositInfo?.methods?.eur?.minAmount || 500;
      const max = depositInfo?.methods?.eur?.maxAmount || 10000;
      if (eur < min) { setError(`Минимум €${min}`); setCalculation(null); return; }
      if (eur > max) { setError(`Максимум €${max}`); setCalculation(null); return; }
      setCalculation(calculateEur(eur));
    } else if (method === 'usdt' && value && !isNaN(value)) {
      const usdt = parseFloat(value);
      const limits = getUsdtLimits();
      if (usdt < limits.min) { setError(`Минимум ${limits.min} DAR`); return; }
      if (usdt > limits.max) { setError(`Максимум ${limits.max} DAR`); return; }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!amount || isNaN(amount)) { setError('Введите сумму'); return; }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const token = localStorage.getItem('token');
      const endpoint = method === 'usdt' ? `${API_URL}/api/crypto/deposit/usdt` : `${API_URL}/api/crypto/deposit/eur`;
      const body = { amount: parseFloat(amount) };
      if (method === 'usdt') body.network = selectedNetwork;

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(body)
      });

      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.message || 'Ошибка создания заявки');

      setCreatedDeposit(data.data);
      setSuccess('Заявка создана!');
      setAmount('');
      setCalculation(null);
      fetchHistory();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmTx = async () => {
    if (!txHash || !createdDeposit) { setError('Введите txHash транзакции'); return; }

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/crypto/deposit/usdt/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ depositId: createdDeposit.depositId, txHash })
      });

      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.message || 'Ошибка подтверждения');

      setSuccess(`✅ Депозит подтверждён! +${data.data.amount} DAR`);
      setCreatedDeposit(null);
      setTxHash('');
      fetchHistory();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { class: 'badge-pending', text: '⏳ Ожидает' },
      awaiting_payment: { class: 'badge-awaiting', text: '💳 Ожидает оплаты' },
      confirmed: { class: 'badge-success', text: '✅ Подтверждён' },
      failed: { class: 'badge-error', text: '❌ Ошибка' },
      expired: { class: 'badge-expired', text: '⏰ Истёк' }
    };
    return badges[status] || { class: 'badge-default', text: status };
  };

  const limits = getUsdtLimits();
  const currentNetwork = getCurrentNetwork();

  return (
    <div className="business-deposit">
      <h2>💰 Пополнить бизнес-счёт</h2>

      <div className="method-tabs">
        <button className={`tab ${method === 'eur' ? 'active' : ''}`}
          onClick={() => { setMethod('eur'); setAmount(''); setCalculation(null); setCreatedDeposit(null); }}>
          <span className="tab-icon">🏦</span>EUR банковский<span className="tab-fee">Рекомендуется</span>
        </button>
        <button className={`tab ${method === 'usdt' ? 'active' : ''}`}
          onClick={() => { setMethod('usdt'); setAmount(''); setCalculation(null); setCreatedDeposit(null); }}>
          <span className="tab-icon">💎</span>Купить DAR за USDT<span className="tab-fee">0% комиссии</span>
        </button>
      </div>

      {/* EUR форма */}
      {method === 'eur' && !createdDeposit && (
        <form onSubmit={handleSubmit} className="deposit-form">
          <div className="info-box">
            <h4>🏦 Банковский перевод Купить DAR за EUR</h4>
            <p>Идеально для бизнеса: безопасно, с документами, отчётность</p>
          </div>
          <div className="form-group">
            <label>Сумма EUR</label>
            <div className="input-wrapper">
              <input type="number" value={amount} onChange={handleAmountChange} placeholder="500 - 10000" min="500" max="10000" step="1" disabled={loading} />
              <span className="currency">€</span>
            </div>
            <small>Мин: €500 | Макс: €10,000</small>
          </div>
          {calculation && (
            <div className="calculation">
              <div className="calc-row"><span>Вы переводите:</span><strong>€{calculation.eur}</strong></div>
              <div className="calc-row"><span>Курс конвертации:</span><strong>{calculation.rate}</strong></div>
              <div className="calc-row"><span>Спред платформы:</span><strong>{calculation.spread}%</strong></div>
              <div className="calc-row"><span>Банковская комиссия:</span><strong>€{calculation.bankFee}</strong></div>
              <div className="calc-row total"><span>На баланс поступит:</span><strong>{calculation.usdt} DAR</strong></div>
            </div>
          )}
          <button type="submit" disabled={loading || !amount || !calculation} className="btn-primary">
            {loading ? '⏳ Создание...' : '📝 Создать заявку на пополнение'}
          </button>
        </form>
      )}

      {/* EUR инструкции */}
      {method === 'eur' && createdDeposit && (
        <div className="deposit-instructions">
          <div className="success-header"><span className="icon">✅</span><h3>Заявка на пополнение создана!</h3></div>
          <div className="bank-details">
            <h4>💳 Реквизиты для банковского перевода:</h4>
            <div className="detail-row"><span>Получатель:</span><strong>{createdDeposit.bankDetails?.name}</strong></div>
            <div className="detail-row"><span>IBAN:</span><div className="copyable"><strong>{createdDeposit.bankDetails?.iban}</strong><button onClick={() => navigator.clipboard.writeText(createdDeposit.bankDetails?.iban)}>📋</button></div></div>
            <div className="detail-row"><span>BIC/SWIFT:</span><strong>{createdDeposit.bankDetails?.bic}</strong></div>
            <div className="detail-row"><span>Банк:</span><strong>{createdDeposit.bankDetails?.bank}</strong></div>
            <div className="detail-row highlight"><span>⚠️ Назначение платежа:</span><div className="copyable"><strong>{createdDeposit.bankDetails?.reference}</strong><button onClick={() => navigator.clipboard.writeText(createdDeposit.bankDetails?.reference)}>📋</button></div></div>
            <div className="detail-row highlight"><span>Сумма к переводу:</span><strong>€{createdDeposit.amountEur}</strong></div>
          </div>
          <div className="info-note">
            <p>⚠️ <strong>Важно:</strong> Укажите назначение платежа точно как указано!</p>
            <p>✅ После получения средств мы зачислим <strong>{createdDeposit.finalUsdt} DAR</strong></p>
            <p>⏱ Срок обработки: {createdDeposit.processingTime}</p>
          </div>
          <button onClick={() => setCreatedDeposit(null)} className="btn-secondary">← Создать новую заявку</button>
        </div>
      )}

      {/* USDT форма */}
      {method === 'usdt' && !createdDeposit && (
        <form onSubmit={handleSubmit} className="deposit-form">
          <div className="info-box">
            <h4>💎 Купить DAR за USDT</h4>
            <p>Быстро и без комиссии платформы</p>
          </div>

          {networks.length > 0 && (
            <div className="form-group">
              <label>Сеть</label>
              <div className="network-selector">
                {networks.map(net => (
                  <button key={net.code} type="button" className={`network-btn ${selectedNetwork === net.code ? 'active' : ''}`}
                    onClick={() => setSelectedNetwork(net.code)}>
                    <span className="network-name">{net.name}</span>
                    <span className="network-symbol">{net.nativeSymbol}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="form-group">
            <label>Сумма DAR</label>
            <div className="input-wrapper">
              <input type="number" value={amount} onChange={handleAmountChange} placeholder={`${limits.min} - ${limits.max}`} min={limits.min} max={limits.max} step="0.01" disabled={loading} />
              <span className="currency">USDT</span>
            </div>
            <small>Мин: {limits.min} USDT | Макс: {limits.max.toLocaleString()} USDT | Комиссия: 0%</small>
          </div>

          <button type="submit" disabled={loading || !amount} className="btn-primary">
            {loading ? '⏳ Создание...' : '📝 Получить адрес для пополнения'}
          </button>
        </form>
      )}

      {/* USDT инструкции */}
      {method === 'usdt' && createdDeposit && (
        <div className="deposit-instructions">
          <div className="success-header"><span className="icon">✅</span><h3>Заявка создана!</h3></div>
          <div className="wallet-box">
            <label>Отправьте {createdDeposit.expectedAmount} USDT на адрес:</label>
            <div className="wallet-address">
              <code>{createdDeposit.walletAddress}</code>
              <button onClick={() => navigator.clipboard.writeText(createdDeposit.walletAddress)}>📋 Копировать</button>
            </div>
            <small>⚠️ Сеть: {createdDeposit.networkName || currentNetwork?.name || 'TRON'}</small>
          </div>
          <div className="form-group">
            <label>После отправки введите Transaction Hash:</label>
            <input type="text" value={txHash} onChange={(e) => setTxHash(e.target.value)} placeholder="Вставьте txHash транзакции..." disabled={loading} />
          </div>
          <button onClick={handleConfirmTx} disabled={loading || !txHash} className="btn-primary">
            {loading ? '⏳ Проверка...' : '✅ Подтвердить депозит'}
          </button>
          {createdDeposit.explorerUrl && (
            <a href={createdDeposit.explorerUrl} target="_blank" rel="noopener noreferrer" className="explorer-link">🔍 Открыть в Explorer</a>
          )}
        </div>
      )}

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {history.length > 0 && (
        <div className="history-section">
          <h3>📜 История пополнений</h3>
          <div className="history-list">
            {history.slice(0, 5).map((dep) => {
              const badge = getStatusBadge(dep.status);
              return (
                <div key={dep.id} className="history-item">
                  <div className="item-main">
                    <span className="method">{dep.method === 'usdt' ? '💎' : '🏦'}</span>
                    <span className="amount">{dep.method === 'usdt' ? `${dep.expected_amount} USDT` : `€${dep.expected_amount}`}</span>
                    {dep.chain_code && dep.method === 'usdt' && <span className="network-badge">{dep.chain_code.toUpperCase()}</span>}
                    <span className={`badge ${badge.class}`}>{badge.text}</span>
                  </div>
                  <div className="item-date">{new Date(dep.created_at).toLocaleDateString('ru-RU')}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessDeposit;